import { useAuth0 } from "@auth0/auth0-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Paper, Typography } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ReservationForm, {
    type ReservationFormData,
    useReservationSchema,
} from "@/components/Owners/ReservationForm";
import PageHeader from "@/components/PageHeader";
import SuccessToast from "@/components/SuccessToast";
import { useCreateReservationMutation } from "@/mutations/reservation.ts";
import { useQueryOptionsFactory } from "@/queries";
import type { ListReservation } from "@/queries/reservations.ts";
import type { ListUnit } from "@/queries/units.ts";

export const Route = createFileRoute("/owner/reservation/book")({
    component: RouteComponent,
});

const generateConfirmationNumber = () => {
    return Math.floor(Math.random() * 900000) + 100000;
};

function RouteComponent() {
    const { user } = useAuth0();

    if (!user?.email) {
        throw new Error("Auth0 user with no email.");
    }

    const qof = useQueryOptionsFactory();
    const { data: reservationsData } = useSuspenseQuery(qof.reservations.listCurrentAndFuture());
    const { data } = useSuspenseQuery(qof.units.list());
    const units = data.data;
    const reservations = reservationsData.data;
    return <DisplayComponent units={units} reservations={reservations} />;
}

type Props = {
    units: ListUnit[];
    reservations: ListReservation[];
};
const DisplayComponent = ({ units, reservations }: Props) => {
    const navigate = useNavigate();
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const reservationSchema = useReservationSchema(reservations);
    const form = useForm<ReservationFormData>({
        resolver: zodResolver(reservationSchema),
        defaultValues: {
            unitNumber: units[0]?.id,
            inputBy: "owner",
            customerFirstName: "",
            customerName: "",
            email: "",
            OtherGuests: "",
            startDate: new Date(),
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            confirmationNumber: generateConfirmationNumber().toString(),
            carMake: "",
            carLicense: "",
        },
    });
    const createReservation = useCreateReservationMutation();

    const ownerName = `${units[0]?.owner?.ownerFirstName} ${units[0]?.owner?.ownerName}`;

    const unitNumberWatch = form.watch("unitNumber");

    const unitNumber = useMemo(() => {
        const unit = units.find((u) => u.id === unitNumberWatch);
        return unit?.rentalId ?? "";
    }, [unitNumberWatch, units]);

    useEffect(() => {
        if (createReservation.data) {
            setShowSuccessToast(true);
            navigate({
                to: "/owner/reservation",
            }).catch((e) => console.error("failed to navigate", e));
        }
    }, [createReservation.data, navigate]);

    const handleSubmit = (data: ReservationFormData) => {
        createReservation.mutate({
            attributes: data,
            type: "reservation",
        });
    };

    const instructions = [
        `To manually add a reservation for Unit ${unitNumber} fill in the form below and click the Save button.`,
        "Dates are for DAYS guests will check-in and check-out.",
        "These should be for Confirmed reservations.",
        "The Confirmation number is automatically assigned and will also be the Parking Permit number.",
    ];

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title={`Aloha ${ownerName}, Owner of Unit ${unitNumber}`}
                    instructions={instructions}
                />

                <Box
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        backgroundColor: "white",
                    }}
                >
                    {/* Guest Reservation Information Header */}
                    <Box
                        sx={{
                            mb: 4,
                            pb: 2,
                            borderBottom: "2px solid #e2e8f0",
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: "#1e293b",
                                mb: 1,
                                fontSize: { xs: "1.25rem", md: "1.5rem" },
                            }}
                        >
                            Guest Reservation Information
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#64748b",
                                fontSize: "0.9rem",
                            }}
                        >
                            Fill in the form below to create a new confirmed reservation
                        </Typography>
                    </Box>
                    <FormProvider {...form}>
                        <ReservationForm
                            units={units}
                            handleSubmit={handleSubmit}
                            disableSubmit={
                                createReservation.isPending || createReservation.data !== undefined
                            }
                        />
                    </FormProvider>
                </Box>
            </Paper>

            <SuccessToast
                open={showSuccessToast}
                onClose={() => setShowSuccessToast(false)}
                title="Reservation Booked Successfully!"
                message={`Guest reservation has been confirmed for Unit ${form.watch("unitNumber") || "selected"}.`}
            />
        </Box>
    );
};
